import {
  buttonAnimation,
  type ButtonAnimation,
  type FieldType,
  type FormConfig,
} from '@cod/config-schema';
import { Box, Text } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { useFieldProblem } from './ProblemsContext.js';
import {
  CheckboxControl,
  ColorControl,
  NumberControl,
  SelectControl,
  SliderControl,
  TextControl,
} from '../components/controls.js';

type Update = (updater: (prev: FormConfig) => FormConfig) => void;

interface BlockProps {
  config: FormConfig;
  update: Update;
}

/**
 * The editing controls for each "block" field, shown inside its expandable card in the
 * designer (the same way input fields show their options). Each block edits its rich config
 * from the matching top-level key; the card's eye icon owns visibility, so these panels carry
 * no separate "show" toggle.
 */
function OrderSummaryControls({ config, update }: BlockProps): JSX.Element {
  const { t } = useTranslation();
  const pc = config.product_card;
  const setPc = (patch: Partial<FormConfig['product_card']>): void =>
    update((c) => ({ ...c, product_card: { ...c.product_card, ...patch } }));
  return (
    <Box display="flex" flexDirection="column" gap="4">
      <Box display="flex" gap="4" flexWrap="wrap">
        <ColorControl
          id="pc-bg"
          label={t('designer.productCard.bgColor')}
          value={pc.bg_color}
          onChange={(bg_color) => setPc({ bg_color })}
        />
        <ColorControl
          id="pc-border"
          label={t('designer.productCard.borderColor')}
          value={pc.border_color}
          onChange={(border_color) => setPc({ border_color })}
        />
      </Box>
      <SliderControl
        label={t('designer.productCard.borderWidth')}
        value={pc.border_width}
        max={20}
        onChange={(border_width) => setPc({ border_width })}
      />
      <SliderControl
        label={t('designer.productCard.borderRadius')}
        value={pc.border_radius}
        max={40}
        onChange={(border_radius) => setPc({ border_radius })}
      />
      <CheckboxControl
        name="pc-hide-qc"
        label={t('designer.productCard.hideQuantity')}
        checked={pc.hide_quantity_controls}
        onChange={(hide_quantity_controls) => setPc({ hide_quantity_controls })}
      />
      <Box display="flex" gap="4" flexWrap="wrap">
        <ColorControl
          id="pc-qc-bg"
          label={t('designer.productCard.qcBg')}
          value={pc.qc_bg_color}
          onChange={(qc_bg_color) => setPc({ qc_bg_color })}
        />
        <ColorControl
          id="pc-qc-border"
          label={t('designer.productCard.qcBorderColor')}
          value={pc.qc_border_color}
          onChange={(qc_border_color) => setPc({ qc_border_color })}
        />
      </Box>
      <SliderControl
        label={t('designer.productCard.qcBorderWidth')}
        value={pc.qc_border_width}
        max={20}
        onChange={(qc_border_width) => setPc({ qc_border_width })}
      />
      <SliderControl
        label={t('designer.productCard.qcBorderRadius')}
        value={pc.qc_border_radius}
        max={40}
        onChange={(qc_border_radius) => setPc({ qc_border_radius })}
      />
    </Box>
  );
}

function TotalsSummaryControls({ config, update }: BlockProps): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  const ts = config.totals_style;
  const setTs = (patch: Partial<FormConfig['totals_style']>): void =>
    update((c) => ({ ...c, totals_style: { ...c.totals_style, ...patch } }));
  return (
    <Box display="flex" flexDirection="column" gap="4">
      <TextControl
        id="ts-subtotal"
        label={t('designer.totals.subtotalTitle')}
        value={ts.subtotal_title}
        error={problem('totals_style.subtotal_title')}
        onChange={(subtotal_title) => setTs({ subtotal_title })}
      />
      <TextControl
        id="ts-shipping"
        label={t('designer.totals.shippingTitle')}
        value={ts.shipping_title}
        error={problem('totals_style.shipping_title')}
        onChange={(shipping_title) => setTs({ shipping_title })}
      />
      <TextControl
        id="ts-total"
        label={t('designer.totals.totalTitle')}
        value={ts.total_title}
        error={problem('totals_style.total_title')}
        onChange={(total_title) => setTs({ total_title })}
      />
      <CheckboxControl
        name="ts-show-taxes"
        label={t('designer.totals.showTaxes')}
        checked={ts.show_taxes}
        onChange={(show_taxes) => setTs({ show_taxes })}
      />
      {ts.show_taxes && (
        <TextControl
          id="ts-taxes-msg"
          label={t('designer.totals.taxesMessage')}
          value={ts.taxes_message}
          error={problem('totals_style.taxes_message')}
          onChange={(taxes_message) => setTs({ taxes_message })}
        />
      )}
      <Box display="flex" gap="4" flexWrap="wrap">
        <ColorControl
          id="ts-bg"
          label={t('designer.totals.bgColor')}
          value={ts.bg_color}
          onChange={(bg_color) => setTs({ bg_color })}
        />
        <ColorControl
          id="ts-text"
          label={t('designer.totals.textColor')}
          value={ts.text_color}
          onChange={(text_color) => setTs({ text_color })}
        />
        <ColorControl
          id="ts-border"
          label={t('designer.totals.borderColor')}
          value={ts.border_color}
          onChange={(border_color) => setTs({ border_color })}
        />
      </Box>
      <SliderControl
        label={t('designer.totals.borderWidth')}
        value={ts.border_width}
        max={20}
        onChange={(border_width) => setTs({ border_width })}
      />
      <SliderControl
        label={t('designer.totals.borderRadius')}
        value={ts.border_radius}
        max={40}
        onChange={(border_radius) => setTs({ border_radius })}
      />
      <Box display="flex" gap="4" flexWrap="wrap">
        <ColorControl
          id="ts-divider"
          label={t('designer.totals.dividerColor')}
          value={ts.divider_color}
          onChange={(divider_color) => setTs({ divider_color })}
        />
      </Box>
      <SliderControl
        label={t('designer.totals.dividerWidth')}
        value={ts.divider_width}
        max={20}
        onChange={(divider_width) => setTs({ divider_width })}
      />
    </Box>
  );
}

function ShippingMethodControls({ config, update }: BlockProps): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  const sm = config.shipping_method;
  const setShipping = (patch: Partial<FormConfig['shipping_method']>): void =>
    update((c) => ({ ...c, shipping_method: { ...c.shipping_method, ...patch } }));
  // `shipping_label` is the free/zero-cost label shown on a rate card and in the totals box.
  const setSummary = (patch: Partial<FormConfig['summary']>): void =>
    update((c) => ({ ...c, summary: { ...c.summary, ...patch } }));
  return (
    <Box display="flex" flexDirection="column" gap="4">
      <TextControl
        id="shipping-method-title"
        label={t('designer.text.shippingMethodTitle')}
        value={sm.title}
        error={problem('shipping_method.title')}
        onChange={(title) => setShipping({ title })}
      />
      <TextControl
        id="shipping-method-free"
        label={t('designer.shipping.freeText')}
        value={config.summary.shipping_label}
        error={problem('summary.shipping_label')}
        onChange={(shipping_label) => setSummary({ shipping_label })}
      />
      <Box display="flex" gap="4" flexWrap="wrap">
        <ColorControl
          id="sm-bg"
          label={t('designer.shipping.bgColor')}
          value={sm.bg_color}
          onChange={(bg_color) => setShipping({ bg_color })}
        />
        <ColorControl
          id="sm-text"
          label={t('designer.shipping.textColor')}
          value={sm.text_color}
          onChange={(text_color) => setShipping({ text_color })}
        />
        <ColorControl
          id="sm-border"
          label={t('designer.shipping.borderColor')}
          value={sm.border_color}
          onChange={(border_color) => setShipping({ border_color })}
        />
        <ColorControl
          id="sm-active"
          label={t('designer.shipping.activeBorderColor')}
          value={sm.active_border_color}
          onChange={(active_border_color) => setShipping({ active_border_color })}
        />
      </Box>
      <SliderControl
        label={t('designer.shipping.borderWidth')}
        value={sm.border_width}
        max={20}
        onChange={(border_width) => setShipping({ border_width })}
      />
      <SliderControl
        label={t('designer.shipping.borderRadius')}
        value={sm.border_radius}
        max={40}
        onChange={(border_radius) => setShipping({ border_radius })}
      />
      <HintControls message={t('designer.blocks.shippingRatesHint')} />
    </Box>
  );
}

function DiscountControls({ config, update }: BlockProps): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  const d = config.discount;
  const set = (patch: Partial<FormConfig['discount']>): void =>
    update((c) => ({ ...c, discount: { ...c.discount, ...patch } }));
  return (
    <Box display="flex" flexDirection="column" gap="4">
      {/* 🚨 No "Limit to 1 discount code per order" checkbox, and no error text for it. One code
          per order was never optional — the block has a single input and applying a code disables
          it — so the switch offered a choice the form does not have. The rule it really governed
          (a coupon on top of a quantity-offer discount) now always applies; see `limit_one` in
          `config-schema`. Removed 2026-08-17. */}
      <TextControl
        id="disc-line"
        label={t('designer.discount.lineText')}
        value={d.line_text}
        error={problem('discount.line_text')}
        onChange={(line_text) => set({ line_text })}
      />
      <TextControl
        id="disc-field-label"
        label={t('designer.discount.fieldLabel')}
        value={d.field_label}
        error={problem('discount.field_label')}
        onChange={(field_label) => set({ field_label })}
      />
      <TextControl
        id="disc-apply"
        label={t('designer.discount.applyText')}
        value={d.apply_text}
        error={problem('discount.apply_text')}
        onChange={(apply_text) => set({ apply_text })}
      />
      {/* Apply button styling */}
      <Box display="flex" gap="4" flexWrap="wrap">
        <ColorControl
          id="disc-btn-bg"
          label={t('designer.discount.buttonBg')}
          value={d.button_bg_color}
          onChange={(button_bg_color) => set({ button_bg_color })}
        />
        <ColorControl
          id="disc-btn-text"
          label={t('designer.discount.buttonText')}
          value={d.button_text_color}
          onChange={(button_text_color) => set({ button_text_color })}
        />
        <ColorControl
          id="disc-btn-border"
          label={t('designer.discount.borderColor')}
          value={d.button_border_color}
          onChange={(button_border_color) => set({ button_border_color })}
        />
      </Box>
      <SliderControl
        label={t('designer.discount.borderRadius')}
        value={d.button_border_radius}
        max={40}
        onChange={(button_border_radius) => set({ button_border_radius })}
      />
      <SliderControl
        label={t('designer.discount.borderWidth')}
        value={d.button_border_width}
        max={20}
        onChange={(button_border_width) => set({ button_border_width })}
      />
      {/* Input styling */}
      <Box display="flex" gap="4" flexWrap="wrap">
        <ColorControl
          id="disc-input-bg"
          label={t('designer.discount.inputBg')}
          value={d.input_bg_color}
          onChange={(input_bg_color) => set({ input_bg_color })}
        />
        <ColorControl
          id="disc-input-text"
          label={t('designer.discount.inputText')}
          value={d.input_text_color}
          onChange={(input_text_color) => set({ input_text_color })}
        />
        <ColorControl
          id="disc-input-border"
          label={t('designer.discount.inputBorderColor')}
          value={d.input_border_color}
          onChange={(input_border_color) => set({ input_border_color })}
        />
        <ColorControl
          id="disc-input-placeholder"
          label={t('designer.discount.inputPlaceholderColor')}
          value={d.input_placeholder_color}
          onChange={(input_placeholder_color) => set({ input_placeholder_color })}
        />
      </Box>
      <SliderControl
        label={t('designer.discount.inputBorderRadius')}
        value={d.input_border_radius}
        max={40}
        onChange={(input_border_radius) => set({ input_border_radius })}
      />
      <SliderControl
        label={t('designer.discount.inputBorderWidth')}
        value={d.input_border_width}
        max={20}
        onChange={(input_border_width) => set({ input_border_width })}
      />
      {/* Error messages */}
      <TextControl
        id="disc-invalid"
        label={t('designer.discount.invalidError')}
        value={d.invalid_error}
        error={problem('discount.invalid_error')}
        onChange={(invalid_error) => set({ invalid_error })}
      />
    </Box>
  );
}

function SubmitControls({ config, update }: BlockProps): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  const s = config.submit;
  const setSubmit = (patch: Partial<FormConfig['submit']>): void =>
    update((c) => ({ ...c, submit: { ...c.submit, ...patch } }));
  const setStyle = (patch: Partial<FormConfig['submit']['style']>): void =>
    update((c) => ({ ...c, submit: { ...c.submit, style: { ...c.submit.style, ...patch } } }));
  return (
    <Box display="flex" flexDirection="column" gap="4">
      <TextControl
        id="submit-text"
        label={t('designer.submit.text')}
        helpText={t('designer.submit.textHelp')}
        value={s.text}
        error={problem('submit.text')}
        onChange={(text) => setSubmit({ text })}
      />
      <TextControl
        id="submit-subtitle"
        label={t('designer.custom.subtitle')}
        value={s.subtitle}
        error={problem('submit.subtitle')}
        onChange={(subtitle) => setSubmit({ subtitle })}
      />
      <SelectControl<ButtonAnimation>
        id="submit-anim"
        label={t('designer.custom.animation')}
        value={s.style.animation}
        options={buttonAnimation.options.map((a) => ({
          value: a,
          label: t(`designer.custom.anim_${a}`),
        }))}
        onChange={(animation) => setStyle({ animation })}
      />
      <Box display="flex" gap="4" flexWrap="wrap">
        <ColorControl
          id="submit-bg"
          label={t('designer.submit.bgColor')}
          value={s.style.bg_color}
          onChange={(bg_color) => setStyle({ bg_color })}
        />
        <ColorControl
          id="submit-text-color"
          label={t('designer.submit.textColor')}
          value={s.style.text_color}
          onChange={(text_color) => setStyle({ text_color })}
        />
        <ColorControl
          id="submit-border"
          label={t('designer.custom.borderColor')}
          value={s.style.border_color}
          onChange={(border_color) => setStyle({ border_color })}
        />
      </Box>
      <SliderControl
        label={t('designer.submit.borderRadius')}
        value={s.style.border_radius ?? config.appearance.border_radius}
        max={100}
        onChange={(border_radius) => setStyle({ border_radius })}
      />
      <SliderControl
        label={t('designer.custom.borderWidth')}
        value={s.style.border_width}
        max={20}
        onChange={(border_width) => setStyle({ border_width })}
      />
      <SliderControl
        label={t('designer.custom.shadow')}
        value={s.style.shadow}
        max={40}
        onChange={(shadow) => setStyle({ shadow })}
      />
      {/* 🚨 `min={8}` is not cosmetic: `submitStyle.font_size` is `.min(8).max(40)`, so the
          missing floor let the merchant pick a size the schema rejects — the save then failed
          with "Could not save changes" and nothing said which control was at fault. Matches the
          terms and newsletter font controls below, which already carried it. */}
      <NumberControl
        id="submit-font"
        label={t('designer.submit.fontSize')}
        value={s.style.font_size}
        error={problem('submit.style.font_size')}
        min={8}
        max={40}
        onChange={(font_size) => setStyle({ font_size })}
      />
    </Box>
  );
}

function TermsControls({ config, update }: BlockProps): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  const terms = config.consent.terms;
  const setTerms = (patch: Partial<FormConfig['consent']['terms']>): void =>
    update((c) => ({ ...c, consent: { ...c.consent, terms: { ...c.consent.terms, ...patch } } }));
  const setStyle = (patch: Partial<FormConfig['consent']['terms']['style']>): void =>
    update((c) => ({
      ...c,
      consent: {
        ...c.consent,
        terms: { ...c.consent.terms, style: { ...c.consent.terms.style, ...patch } },
      },
    }));
  return (
    <Box display="flex" flexDirection="column" gap="4">
      <TextControl
        id="terms-label"
        label={t('designer.text.terms')}
        value={terms.label}
        error={problem('consent.terms.label')}
        onChange={(label) => setTerms({ label })}
      />
      <TextControl
        id="terms-url"
        label={t('designer.text.termsUrl')}
        value={terms.url ?? ''}
        error={problem('consent.terms.url')}
        onChange={(url) => setTerms({ url: url || null })}
      />
      <CheckboxControl
        name="terms-required"
        label={t('designer.fields.required')}
        checked={terms.required}
        onChange={(required) => setTerms({ required })}
      />
      {/* Only meaningful while acceptance is enforced — there is no error to word otherwise. */}
      {terms.required && (
        <Box display="flex" flexDirection="column" gap="1">
          <TextControl
            id="terms-error"
            label={t('designer.fields.errorText')}
            value={terms.error_text}
            error={problem('consent.terms.error_text')}
            onChange={(error_text) => setTerms({ error_text })}
          />
          <Text fontSize="caption" color="neutral-textLow">
            {t('designer.terms.errorHelp')}
          </Text>
        </Box>
      )}
      <Box display="flex" gap="4" flexWrap="wrap">
        <ColorControl
          id="terms-text"
          label={t('designer.terms.textColor')}
          value={terms.style.text_color}
          onChange={(text_color) => setStyle({ text_color })}
        />
        <ColorControl
          id="terms-link"
          label={t('designer.terms.linkColor')}
          value={terms.style.link_color}
          onChange={(link_color) => setStyle({ link_color })}
        />
      </Box>
      <NumberControl
        id="terms-font"
        label={t('designer.terms.fontSize')}
        value={terms.style.font_size}
        min={8}
        max={40}
        onChange={(font_size) => setStyle({ font_size })}
      />
    </Box>
  );
}

function NewsletterControls({ config, update }: BlockProps): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  const nl = config.consent.newsletter;
  const setNl = (patch: Partial<FormConfig['consent']['newsletter']>): void =>
    update((c) => ({
      ...c,
      consent: { ...c.consent, newsletter: { ...c.consent.newsletter, ...patch } },
    }));
  const setStyle = (patch: Partial<FormConfig['consent']['newsletter']['style']>): void =>
    update((c) => ({
      ...c,
      consent: {
        ...c.consent,
        newsletter: { ...c.consent.newsletter, style: { ...c.consent.newsletter.style, ...patch } },
      },
    }));
  return (
    <Box display="flex" flexDirection="column" gap="4">
      <TextControl
        id="newsletter-text"
        label={t('designer.newsletter.text')}
        value={nl.text}
        error={problem('consent.newsletter.text')}
        onChange={(text) => setNl({ text })}
      />
      <ColorControl
        id="newsletter-color"
        label={t('designer.newsletter.textColor')}
        value={nl.style.text_color}
        onChange={(text_color) => setStyle({ text_color })}
      />
      <NumberControl
        id="newsletter-font"
        label={t('designer.newsletter.fontSize')}
        value={nl.style.font_size}
        error={problem('consent.newsletter.style.font_size')}
        min={8}
        max={40}
        onChange={(font_size) => setStyle({ font_size })}
      />
    </Box>
  );
}

function HintControls({ message }: { message: string }): JSX.Element {
  return (
    <Text fontSize="caption" color="neutral-textLow">
      {message}
    </Text>
  );
}

/**
 * Render the editing controls for a block field by its type. Returns null for input field
 * types (the regular field controls handle those).
 */
export function BlockControls({
  type,
  config,
  update,
}: {
  type: FieldType;
  config: FormConfig;
  update: Update;
}): JSX.Element | null {
  switch (type) {
    case 'order_summary':
      return <OrderSummaryControls config={config} update={update} />;
    case 'totals_summary':
      return <TotalsSummaryControls config={config} update={update} />;
    case 'shipping_method':
      return <ShippingMethodControls config={config} update={update} />;
    case 'discount_code':
      return <DiscountControls config={config} update={update} />;
    case 'submit':
      return <SubmitControls config={config} update={update} />;
    case 'terms':
      return <TermsControls config={config} update={update} />;
    case 'newsletter':
      return <NewsletterControls config={config} update={update} />;
    case 'quantity_offers':
      return <QuantityOffersHint />;
    default:
      return null;
  }
}

function QuantityOffersHint(): JSX.Element {
  const { t } = useTranslation();
  return <HintControls message={t('designer.blocks.quantityOffersHint')} />;
}
